// Rendering condiviso della card "confronto tra regimi", usata sia dalla pagina
// dei risultati ordinari sia da quella forfettaria. Le due pagine hanno lo
// stesso markup (stessi id), quindi la logica di riempimento e' una sola.
import { SOGLIA_FORFETTARIO, SOGLIA_USCITA_IMMEDIATA } from './confrontoRegimi.js';

const euro = (value) =>
    `${new Intl.NumberFormat('it-IT', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: 'always'
    }).format(value)} €`;

const encode = (obj) => {
    try {
        return btoa(encodeURIComponent(JSON.stringify(obj)));
    } catch (e) {
        console.error('encode confronto failed', e);
        return null;
    }
};

const setText = (id, value) => {
    const el = document.getElementById(id);
    if (el) el.textContent = value;
};
const setHtml = (id, value) => {
    const el = document.getElementById(id);
    if (el) el.innerHTML = value;
};

// Link al calcolatore ordinario precompilato con quel che sappiamo.
export function linkAllOrdinario(p) {
    const payload = {
        ricavi: Math.round(p.ricavi || 0),
        costi: Math.round(p.costi || 0),
        formaAttivita: p.formaAttivita || '',
        cassa: p.cassa || '',
        regione: p.regione || '',
        comune: p.comune || { aliquota: 0.8 },
        dipendenteFullTime: Boolean(p.dipendenteFullTime),
        riduzioneDipendente: Boolean(p.riduzioneDipendente),
        ralDipendente: Math.round(p.ralDipendente || 0),
        mesiAttivita: p.mesiAttivita || 12,
        nuovaAttivitaArtCom: Boolean(p.nuovaAttivitaArtCom),
        riduzione50: Boolean(p.riduzione50),
        ante1996: Boolean(p.ante1996),
        numeroMandanti: p.numeroMandanti || 1,
        fasciaEtaEnpam: p.fasciaEtaEnpam || 'over40',
        contabilitaOrdinaria: Boolean(p.contabilitaOrdinaria)
    };
    const d = encode(payload);
    return d ? `/piva-ordinaria?d=${encodeURIComponent(d)}` : '/piva-ordinaria';
}

// Link al calcolatore forfettario. Usa le chiavi storiche di quella pagina.
export function linkAlForfettario(p) {
    const payload = {
        fatturato: String(Math.round(p.ricavi || 0)),
        anniAttivita: String(p.aliquotaImposta || 15),
        tipoAttivita: String(p.coefficienteRedditivita || 78),
        formaAttivita: p.formaAttivita || '',
        cassa: p.cassa || '',
        mesiAttivita: String(p.mesiAttivita || 12),
        nuovaAttivitaArtCom: p.nuovaAttivitaArtCom ? '1' : '0',
        riduzione50: p.riduzione50 ? '1' : '0',
        riduzione35: p.riduzione35 ? '1' : '0',
        dipendenteFullTime: p.dipendenteFullTime ? '1' : '0',
        ralDipendente: String(Math.round(p.ralDipendente || 0)),
        riduzioneDipendente: p.riduzioneDipendente ? '1' : '0'
    };
    const d = encode(payload);
    return d ? `/piva-forfettario?d=${encodeURIComponent(d)}` : '/piva-forfettario';
}

// esito: risultato di confrontaRegimi().
// opts: { origine: 'ordinario' | 'forfettario', ctaHref, notaIpotesi }
export function renderConfronto(esito, opts = {}) {
    const wrapper = document.getElementById('confrontoRegimi');
    if (!wrapper) return;
    const daOrdinario = opts.origine === 'ordinario';
    const { ammissibilita, migliore } = esito;
    const accessibile = ammissibilita.ammissibile;
    // Sopra la soglia il motore calcola il forfettario su 85.000 €, non sui
    // ricavi reali: tutta la copy deve dire che e' lo scenario "se limitassi
    // il fatturato", non un confronto a parita' di numeri.
    const limitato = Boolean(esito.forfettarioLimitato);
    const fatturatoLimite = euro(esito.fatturatoForfettario || 0);

    wrapper.style.display = 'block';

    setText('confrontoTitolo', daOrdinario
        ? (limitato ? 'E se limitassi il fatturato per il forfettario?' : 'Ti conviene il forfettario?')
        : 'E se passassi al regime ordinario?');

    // --- Colonne ------------------------------------------------------------
    setText('confrontoNettoOrdinario', euro(esito.nettoOrdinario));
    setText('confrontoNettoForfettario', euro(esito.nettoForfettario));
    setText('confrontoSubOrdinario', `${euro(Math.round(esito.nettoOrdinario / 12))} al mese`);
    setText('confrontoSubForfettario', `${euro(Math.round(esito.nettoForfettario / 12))} al mese`);

    const colOrd = document.getElementById('confrontoColOrdinario');
    const colFor = document.getElementById('confrontoColForfettario');
    const labelFor = colFor?.querySelector('.piva-vs-label');
    if (labelFor) {
        labelFor.textContent = limitato
            ? `Forfettario a ${fatturatoLimite}`
            : 'Regime forfettario';
    }
    colOrd?.classList.toggle('is-winner', accessibile && migliore === 'ordinario');
    colFor?.classList.toggle('is-winner', accessibile && migliore === 'forfettario');
    // Se il forfettario non e' accessibile la colonna resta, ma smorzata: serve
    // a quantificare cosa si perde, non a suggerire una scelta che non c'e'.
    colFor?.classList.toggle('is-unavailable', !accessibile);

    // --- Verdetto -----------------------------------------------------------
    const icona = document.getElementById('confrontoVerdettoIcona');
    const setIcona = (nome) => { if (icona) icona.className = `ph ${nome}`; };

    if (!accessibile && limitato) {
        // Fatturato oltre la soglia: un forfettario a parita' di ricavi non
        // esiste, quindi il confronto risponde all'unica domanda reale, cioe'
        // "mi converrebbe limitare il fatturato a 85.000 € per restarci?".
        const netto = `<strong>${euro(esito.nettoOrdinario)}</strong>`;
        const nettoFor = `<strong>${euro(esito.nettoForfettario)}</strong>`;
        let ipotesi;
        if (migliore === 'pari') {
            ipotesi = ` Nell'ordinario il netto è ${netto}. Se limitassi il fatturato a ${fatturatoLimite} per rientrare nel forfettario avresti lo stesso netto, pur fatturando meno.`;
        } else if (migliore === 'forfettario') {
            ipotesi = ` Nell'ordinario il netto è ${netto}. Se limitassi il fatturato a ${fatturatoLimite} per rientrare nel forfettario ti resterebbero ${nettoFor}: <strong>${euro(esito.differenzaAssoluta)}</strong> in più all'anno, pur fatturando meno.`;
        } else {
            ipotesi = ` Nell'ordinario il netto è ${netto}. Se limitassi il fatturato a ${fatturatoLimite} per rientrare nel forfettario ti resterebbero ${nettoFor}: <strong>${euro(esito.differenzaAssoluta)}</strong> in meno all'anno, quindi fatturare di più ti conviene anche restando nell'ordinario.`;
        }
        setHtml('confrontoVerdettoTesto',
            `<strong>Il forfettario non è utilizzabile con questo fatturato.</strong> ${ammissibilita.motivo}${ipotesi}`);
        setIcona('ph-warning-circle');
        wrapper.classList.add('is-blocked');
    } else if (!accessibile) {
        // La colonna forfettario resta a schermo anche quando il regime e'
        // precluso, quindi va detto cosa vale quel numero: e' l'ipotesi "se
        // potessi usarlo", non una scelta disponibile. Il segno conta: se a
        // vincere e' l'ordinario, dire che il requisito mancante "ti costa"
        // qualcosa sarebbe falso.
        const netto = `<strong>${euro(esito.nettoOrdinario)}</strong>`;
        let ipotesi;
        if (migliore === 'pari') {
            ipotesi = ` Restando nell'ordinario il netto è ${netto}, lo stesso che ti lascerebbe il forfettario: qui non stai perdendo nulla.`;
        } else if (migliore === 'forfettario') {
            ipotesi = ` Restando nell'ordinario il netto è ${netto}: il forfettario, se potessi usarlo, ti lascerebbe <strong>${euro(esito.differenzaAssoluta)}</strong> in più all'anno, ${euro(esito.differenzaMensile)} al mese.`;
        } else {
            ipotesi = ` Restando nell'ordinario il netto è ${netto}: il forfettario ti lascerebbe <strong>${euro(esito.differenzaAssoluta)}</strong> in meno all'anno, quindi il regime agevolato non ti converrebbe comunque.`;
        }
        setHtml('confrontoVerdettoTesto',
            `<strong>Il forfettario non è utilizzabile.</strong> ${ammissibilita.motivo}${ipotesi}`);
        setIcona('ph-warning-circle');
        wrapper.classList.add('is-blocked');
    } else {
        wrapper.classList.remove('is-blocked');
        setIcona('ph-scales');
        if (migliore === 'pari') {
            setHtml('confrontoVerdettoTesto',
                'Con questi numeri i due regimi ti lasciano <strong>lo stesso netto</strong>: a decidere sono gli adempimenti, l\'IVA e la deducibilità dei costi futuri.');
        } else {
            const vincitore = migliore === 'forfettario' ? 'il forfettario' : "l'ordinario";
            setHtml('confrontoVerdettoTesto',
                `Con questi numeri conviene <strong>${vincitore}</strong>: ` +
                `<strong>${euro(esito.differenzaAssoluta)}</strong> in più all'anno, ` +
                `${euro(esito.differenzaMensile)} al mese.`);
        }
    }

    // --- Intro e nota sulle ipotesi ----------------------------------------
    setHtml('confrontoIntro', limitato
        ? `Il tuo fatturato supera la soglia del forfettario: la colonna del regime agevolato è calcolata su ${fatturatoLimite}, il massimo consentito.`
        : (daOrdinario
            ? `Stesso fatturato, stessa cassa: ecco quanto ti resterebbe nel regime agevolato.`
            : `Stesso fatturato e stessa cassa, ma con i costi reali dedotti e l'IRPEF progressiva al posto dell'imposta sostitutiva.`));

    // Il netto forfettario e' quello del regime, senza costi: la nota lo dice
    // e, quando i costi ci sono, quantifica quanto resterebbe sostenendoli,
    // cosi' il confronto con l'ordinario (che li deduce) resta leggibile.
    const costiNota = Math.max(0, esito.costiReali || 0);
    let noteComuni = 'Il netto forfettario è calcolato come prevede il regime, senza sottrarre i costi reali: nell\'ordinario invece sono già dedotti.';
    if (costiNota > 0) {
        noteComuni += ` Se anche nel forfettario sostenessi gli stessi costi (${euro(costiNota)}), ti resterebbero ${euro(esito.nettoForfettario - costiNota)}.`;
    }
    setHtml('confrontoNota', `${noteComuni} ${opts.notaIpotesi || ''}`);

    // --- CTA ----------------------------------------------------------------
    const cta = document.getElementById('confrontoCta');
    if (cta && opts.ctaHref) {
        cta.href = opts.ctaHref;
        cta.textContent = daOrdinario
            ? 'Apri il calcolo forfettario completo'
            : 'Apri il calcolo ordinario completo';
        cta.style.display = '';
    } else if (cta) {
        cta.style.display = 'none';
    }
}

export { SOGLIA_FORFETTARIO, SOGLIA_USCITA_IMMEDIATA, euro as formatEuroConfronto };
